// One playground request against the data plane's `POST /v1/chat/completions`, with the console's own session: the
// data plane resolves the user token and Nodus-Org like the control plane, requires inference:invoke (the Member
// role and above) and bills the org (internal/inference/caller.go). Streams are read as server-sent events.
import { ApiError, toStatus, type NodusClient } from "@nodus/api";
import type { ChatRequest } from "./snippets";

/** The data plane's body limit for chat completions (internal/inference/allow.go MaxJSONBody). */
export const MAX_CHAT_BODY = 1 << 20;

export type ChatUsage = { prompt_tokens?: number; completion_tokens?: number };

/** What one request has produced so far; the last update is the whole answer. */
export interface ChatOutcome {
  /** The public model submitted for this request, independent of its route or later selection changes. */
  requestedModel: string;
  text: string;
  reasoning: string;
  /** Nodus-Request-Id, the receipt's id. */
  requestId?: string;
  /** The model that answered: X-Nodus-Routed-Model for Indra, else the response's own model. */
  routedModel?: string;
  /** X-Nodus-Composer-Tier, set on Indra responses. */
  tier?: string;
  usage?: ChatUsage;
  finishReason?: string;
  /** Milliseconds from send to the first token of a stream. */
  firstTokenMs?: number;
  /** Milliseconds from send to the end of the answer; unset while it is still arriving. */
  totalMs?: number;
}

type Choice = { delta?: Message; message?: Message; finish_reason?: string | null };
type Message = { content?: string | null; reasoning?: string | null; reasoning_content?: string | null };
type Chunk = { model?: string; choices?: Choice[]; usage?: ChatUsage | null; error?: unknown };

/** The request body's size in bytes, as the data plane measures it. */
export function bodyBytes(req: ChatRequest): number {
  return new TextEncoder().encode(JSON.stringify(req)).length;
}

/** Folds one response object (a stream chunk or the whole answer) into the outcome. */
function fold(out: ChatOutcome, chunk: Chunk): ChatOutcome {
  if (chunk.error) throw new ApiError(502, toStatus(502, chunk), out.requestId);
  const choice = chunk.choices?.[0];
  const part = choice?.delta ?? choice?.message;
  const text = part?.content ?? "";
  const reasoning = part?.reasoning ?? part?.reasoning_content ?? "";
  return {
    ...out,
    text: out.text + text,
    reasoning: out.reasoning + reasoning,
    ...(!out.routedModel && chunk.model ? { routedModel: chunk.model } : {}),
    ...(chunk.usage ? { usage: chunk.usage } : {}),
    ...(choice?.finish_reason ? { finishReason: choice.finish_reason } : {}),
  };
}

/**
 * Sends req for scope's org and project and reports each step through onUpdate. Errors are the API's (ApiError with
 * its reason and message); an abort rejects with the AbortError.
 */
export async function sendChat(
  client: NodusClient,
  scope: { org: string; project: string },
  req: ChatRequest,
  onUpdate: (o: ChatOutcome) => void,
  signal: AbortSignal,
): Promise<ChatOutcome> {
  const start = performance.now();
  const requestedModel = req.model;
  const res = await client.send("POST", "/v1/chat/completions", {
    org: scope.org,
    body: req,
    headers: { "Nodus-Project": scope.project },
    accept: req.stream ? "text/event-stream" : "application/json",
    signal,
  });
  const header = (name: string) => res.headers.get(name) ?? undefined;
  let out: ChatOutcome = { requestedModel, text: "", reasoning: "" };
  const requestId = header("Nodus-Request-Id");
  const routedModel = header("X-Nodus-Routed-Model");
  const tier = header("X-Nodus-Composer-Tier");
  out = { ...out, ...(requestId ? { requestId } : {}), ...(routedModel ? { routedModel } : {}), ...(tier ? { tier } : {}) };
  onUpdate(out);

  if (!req.stream || !res.body) {
    out = { ...fold(out, (await res.json()) as Chunk), totalMs: performance.now() - start };
    onUpdate(out);
    return out;
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    const events = buffer.split(/\r?\n\r?\n/);
    buffer = events.pop() ?? "";
    for (const event of events) {
      const data = event
        .split(/\r?\n/)
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trim())
        .join("\n");
      if (!data || data === "[DONE]") continue;
      const had = out.text.length + out.reasoning.length;
      out = fold(out, JSON.parse(data) as Chunk);
      if (out.firstTokenMs === undefined && out.text.length + out.reasoning.length > had) out.firstTokenMs = performance.now() - start;
      onUpdate(out);
    }
  }
  out = { ...out, totalMs: performance.now() - start };
  onUpdate(out);
  return out;
}
